import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listTasksByAssignee, listTasksByRole } from '../../shared/services/taskService';
import { taskKeys, buildTaskSortKey } from '../../shared/keys';
import type { TaskFilters, TaskListItem, TaskSortField, SortDirection } from '../../shared/types/task';
import { getMyTasks, type MyTasksSummary } from '../../../services/workflowService';

interface UseMyTasksParams {
  userId?: string;
  organizationId?: string;
  filters?: Partial<TaskFilters>;
  sortField?: TaskSortField;
  sortDirection?: SortDirection;
}

interface UseMyTasksResult {
  tasks: TaskListItem[];
  claimedTasks: TaskListItem[];
  availableRoleTasks: TaskListItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  kpis: MyTasksSummary | null;
  kpisLoading: boolean;
}

const DEFAULT_FILTERS: TaskFilters = {
  status: ['pending', 'in_progress', 'paused'],
  priority: [],
  search: '',
  overdue: false,
};

export function useMyTasks(params: UseMyTasksParams): UseMyTasksResult {
  const {
    userId,
    organizationId,
    filters: partialFilters,
    sortField = 'sla_deadline',
    sortDirection = 'asc',
  } = params;

  const [debouncedSearch, setDebouncedSearch] = useState(partialFilters?.search ?? '');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(partialFilters?.search ?? ''), 350);
    return () => clearTimeout(handler);
  }, [partialFilters?.search]);

  const filters: TaskFilters = useMemo(() => ({
    ...DEFAULT_FILTERS,
    ...partialFilters,
    search: debouncedSearch,
  }), [
    debouncedSearch,
    partialFilters?.status,
    partialFilters?.priority,
    partialFilters?.overdue,
  ]);

  const filtersHash = useMemo(
    () => JSON.stringify({
      status: filters.status,
      priority: filters.priority,
      search: filters.search,
      overdue: filters.overdue,
    }),
    [filters],
  );

  const sortKey = useMemo(() => buildTaskSortKey(sortField, sortDirection), [sortField, sortDirection]);

  // Lista de tasks do usuario
  const claimedTasksQuery = useQuery({
    queryKey: taskKeys.byAssignee(organizationId ?? '', userId ?? '', filtersHash, sortKey),
    enabled: Boolean(userId && organizationId),
    queryFn: () => listTasksByAssignee(userId!, organizationId!, filters, sortField, sortDirection),
  });

  const roleQueueQuery = useQuery({
    queryKey: ['tasks', organizationId ?? '', 'byRole', userId ?? '', filtersHash, sortKey],
    enabled: Boolean(userId && organizationId),
    queryFn: () => listTasksByRole(userId!, organizationId!, filters, sortField, sortDirection),
  });

  // KPIs pessoais (reutiliza workflowService.getMyTasks)
  const kpisQuery = useQuery({
    queryKey: ['minha-mesa', organizationId, userId, 'kpis'],
    enabled: Boolean(userId && organizationId),
    queryFn: () => getMyTasks(userId!, organizationId!),
    staleTime: 30_000,
  });

  return {
    tasks: [
      ...(claimedTasksQuery.data ?? []),
      ...((roleQueueQuery.data ?? []).filter((task) =>
        !(claimedTasksQuery.data ?? []).some((claimedTask) => claimedTask.id === task.id),
      )),
    ],
    claimedTasks: claimedTasksQuery.data ?? [],
    availableRoleTasks: roleQueueQuery.data ?? [],
    isLoading: (claimedTasksQuery.isPending && !claimedTasksQuery.data) || (roleQueueQuery.isPending && !roleQueueQuery.data),
    isFetching: claimedTasksQuery.isFetching || roleQueueQuery.isFetching,
    error: claimedTasksQuery.error ?? roleQueueQuery.error ?? null,
    refetch: async () => {
      await Promise.all([claimedTasksQuery.refetch(), roleQueueQuery.refetch()]);
    },
    kpis: kpisQuery.data ?? null,
    kpisLoading: kpisQuery.isPending,
  };
}
